import api from './client';

// Subscriptions API
export const subscriptionsAPI = {
  getAll: (entityId) => api.get('/subscriptions', { params: { entity_id: entityId } }),
  detect: () => api.post('/subscriptions/detect'),
  // Live run-rate grouped Jurisdiction -> Entity -> Vendor. Same population
  // and cadence factors as /subscriptions/run-rate (one backend constant).
  byJurisdiction: ({ entityId, scope, displayCurrency, spendType } = {}) =>
    api.get('/subscriptions/by-jurisdiction', {
      params: {
        entity_id: entityId,
        scope,
        display_currency: displayCurrency,
        spend_type: spendType,
      },
    }),
  // Stat-card figures: one aggregated total in the toggled display currency,
  // split by spend type (subscription / rent / needs review) and by
  // jurisdiction, plus the cancellable estimate.
  summary: ({ entityId, scope, displayCurrency } = {}) =>
    api.get('/subscriptions/summary', {
      params: { entity_id: entityId, scope, display_currency: displayCurrency },
    }),
};

// Recognized subscription vendors (Subscriptions page): a supplier
// invoice from one of these vendors auto-creates/updates ONE Subscription
// record per (entity, vendor) -- services/subscription_vendors.py.
export const subscriptionVendorAPI = {
  list: () => api.get('/subscription-vendors'),
  create: (vendorName) => api.post('/subscription-vendors', { vendor_name: vendorName }),
  remove: (vendorId) => api.delete(`/subscription-vendors/${vendorId}`),
  // Curated aliases (e.g. "Emergent" -> "Emergentagent"): a human-asserted
  // equivalent name for a compound/legal-name variant core-token matching
  // deliberately does not bridge on its own -- see services/
  // subscription_vendors.py module docstring.
  setAliases: (vendorId, aliases) => api.put(`/subscription-vendors/${vendorId}/aliases`, { aliases }),
};

// Confirm-driven alias learning (Prompt: vendor evidence upgrade, Part B):
// every human confirm teaches a tx-supplier-text -> vendor pairing into the
// same recognized-vendor registry above, via services/vendor_alias_learning.py.
export const vendorAliasLearningAPI = {
  removeLearnedAlias: (vendorId, normalizedAlias) => api.delete(
    `/subscription-vendors/${vendorId}/learned-aliases`, { data: { normalized_alias: normalizedAlias } },
  ),
  listConflicts: () => api.get('/subscription-vendors/alias-learning/conflicts'),
  dismissConflict: (normalizedAlias, attemptedVendorId, existingVendorId) => api.delete(
    '/subscription-vendors/alias-learning/conflicts',
    {
      data: {
        normalized_alias: normalizedAlias,
        attempted_vendor_id: attemptedVendorId,
        existing_vendor_id: existingVendorId,
      },
    },
  ),
  backfill: (dryRun = true) => api.post('/subscription-vendors/alias-learning/backfill', { dry_run: dryRun }),
};

// Rebuild Subscriptions From Invoices (Prompt Y backfill card): 202+
// JobStore Preview/Apply over the full recognized-vendor x entity grid --
// routes/admin_subscription_rebuild.py.
export const subscriptionRebuildAPI = {
  start: (dryRun, params = {}) => api.post('/admin/diagnostics/subscription-rebuild/start', { dry_run: dryRun, ...params }),
  jobStatus: (jobId) => api.get(`/admin/diagnostics/subscription-rebuild/job-status/${jobId}`),
  jobLatest: (dryRun) => api.get('/admin/diagnostics/subscription-rebuild/job-latest', { params: { dry_run: dryRun } }),
  // Diagnose-before-fixing field-sample mode: exact join query, invoice
  // population by source, matched count, and nearest-miss supplier names
  // for one recognized vendor x one entity.
  fieldSample: (vendorId, entityId) => api.get('/admin/diagnostics/subscription-rebuild/field-sample', {
    params: { vendor_id: vendorId, entity_id: entityId },
  }),
};
